import React, {
  useRef,
  useCallback,
  useState,
  useEffect,
  useContext,
} from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import cn from 'classnames';
import CalculatorModal from 'chameleon/packages/blue-book-player/InnerContainer/CalculatorModal';
import ReferenceSheetModal from 'chameleon/packages/blue-book-player/InnerContainer/ReferenceSheetModal';
import ExitAssignmentModal from 'chameleon/packages/blue-book-player/library/modal/ExitAssignmentModal';
import SubmitConfirmDialog from 'chameleon/packages/blue-book-player/InnerContainer/SubmitConfirmDialog';
import MoreMenu from 'chameleon/packages/blue-book-player/library/player-menu/MoreMenu';
import LineReaderModal from 'chameleon/packages/blue-book-player/InnerContainer/LineReaderModal';
import MenuItemWithToolTip from './MenuItemWithToolTip';
import MenuItem from './MenuItem';
import referenceSvg from '../icon-list/raw-icons/reference.svg';
import calculatorSvg from '../icon-list/raw-icons/calculator.svg';
import highlightsNotesSvg from '../icon-list/raw-icons/highlights-notes.svg';
import { BluebookContext } from '../../state/BluebookContext';
import { UPDATE_REFERENCE_SHEET } from '../../state/reducer';
import useToggleAnnotation from 'chameleon/packages/blue-book-player/Annotation/hooks/useToggleAnnotation';
import { MENU_ITEM_TOOLTIP } from 'chameleon/packages/blue-book-player/Annotation/constants';
import { IS_HEADER_MENU_ITEM_CALCULATOR_VISIBLE } from 'chameleon/packages/blue-book-player/constants/componentVisibility';
import {
  ReferenceSheetComingSoonSubjects,
  HighlightsNotesHiddenSubjects,
} from 'chameleon/packages/blue-book-player/constants/subjects';
import { isReferenceSheetMenuVisible } from 'chameleon/packages/blue-book-player/InnerContainer/utils';
import HighlightsAndNotesInfoModal from 'chameleon/packages/blue-book-player/library/modal/HighlightsAndNotesInfoModal/HighlightsAndNotesInfoModal';
import ReferenceSheetInfoModal from 'chameleon/packages/blue-book-player/library/modal/ReferenceSheetInfoModal';

import styles from './styles.css';
import MobileMoreMenu from './MobileMoreMenu';
import HelpModal from './HelpModal';
import AnnotationsTeacherModal from './AnnotationsTeacherModal';

function PlayerMenu({
  learnosityApp,
  params,
  questions,
  isPreviewPlayer,
  allowLateSubmissionFF,
  allowHardTimerFF,
  bluebookUiHighlightEnabled,
  isImpersonating,
  isDigitalExam,
  router,
  isTeacher,
}) {
  const { subject } = params;
  const [isAnnotationsTeacherModalOpen, setIsAnnotationTeacherModalOpen] =
    useState(false);
  const [openReferenceSheetModal, setOpenReferenceSheetModal] = useState(false);
  const [openCalculatorModal, setOpenCalculatorModal] = useState(false);
  const [openExitAssignmentModal, setOpenExitAssignmentModal] = useState(false);
  const [openHelpModal, setOpenHelpModal] = useState(false);
  const [openLineReaderModal, setOpenLineReaderModal] = useState(false);
  const [
    isHighlightsAndNotesInfoModalVisible,
    setIsHighlightsAndNotesInfoModalVisible,
  ] = useState(false);
  const [openSubmitConfirmDialog, setOpenSubmitConfirmDialog] = useState(false);
  const [isReferenceSheetButtonSelected, setIsReferenceSheetButtonSelected] =
    useState(false);
  const referenceSheetModalRef = useRef();
  const referenceSheetButtonRef = useRef();
  const calculatorRef = useRef();
  const moreButtonRef = useRef();
  const annotationRef = useRef();
  const {
    state: { referenceSheet, isLearnosityApiReady },
    dispatch,
  } = useContext(BluebookContext);
  const { toggleAnnotation } = useToggleAnnotation();
  const closeAndPersistReferenceSheetModalData = () => {
    const position = referenceSheetModalRef.current.getPosition();
    const width = referenceSheetModalRef.current.getWidth();

    dispatch({
      type: UPDATE_REFERENCE_SHEET,
      payload: {
        lastPosition: position,
        lastWidth: width,
      },
    });

    setOpenReferenceSheetModal(false);
  };

  const onReferenceSheetClick = useCallback(() => {
    setOpenReferenceSheetModal((openModal) => {
      const newOpenModal = !openModal;

      if (!newOpenModal) {
        setIsReferenceSheetButtonSelected(false);
        closeAndPersistReferenceSheetModalData();
      } else {
        setIsReferenceSheetButtonSelected(true);
      }

      return newOpenModal;
    });
  }, [referenceSheetModalRef]);
  const onCalculatorClick = useCallback(() => {
    setOpenCalculatorModal((openModal) => !openModal);
  }, []);

  const onReferenceSheetClose = useCallback(() => {
    closeAndPersistReferenceSheetModalData();
    setIsReferenceSheetButtonSelected(false);
  }, []);

  const onReferenceSheetInfoClose = useCallback(() => {
    setOpenReferenceSheetModal(false);
    setIsReferenceSheetButtonSelected(false);
  }, []);

  const onCalculatorClose = useCallback(() => {
    setOpenCalculatorModal(false);
    calculatorRef?.current?.toggle();
  }, [calculatorRef]);

  const onLineReaderClose = useCallback(() => {
    setOpenLineReaderModal(false);
  }, []);

  const toggleExitAssignmentModal = useCallback(() => {
    setOpenExitAssignmentModal((currentOpenExitAssignmentModal) => {
      if (!currentOpenExitAssignmentModal) {
        moreButtonRef?.current?.focus();
      }
      return !currentOpenExitAssignmentModal;
    });
  });

  const toggleSubmitConfirmDialog = useCallback(() => {
    setOpenSubmitConfirmDialog((currentOpenSubmitConfirmDialog) => {
      if (!currentOpenSubmitConfirmDialog) {
        moreButtonRef?.current?.focus();
      }
      return !currentOpenSubmitConfirmDialog;
    });
  });

  const toggleHelpModal = useCallback(() => {
    setOpenHelpModal((currentOpenHelpModal) => {
      if (!currentOpenHelpModal) {
        moreButtonRef?.current?.focus();
      }
      return !currentOpenHelpModal;
    });
  });

  const onAnnotationClick = () => {
    if (bluebookUiHighlightEnabled) {
      if (isTeacher) {
        setIsAnnotationTeacherModalOpen(true);
        annotationRef.current.close();
      } else {
        toggleAnnotation();
      }
    } else {
      setIsHighlightsAndNotesInfoModalVisible(true);
    }
  };

  useEffect(() => {
    if (!isLearnosityApiReady || !openReferenceSheetModal) {
      return;
    }

    const itemChanging = () => {
      referenceSheetButtonRef?.current?.toggle();
      onReferenceSheetClose();
    };

    learnosityApp.on('item:changing', itemChanging);

    return () => {
      learnosityApp.off('item:changing', itemChanging);
    };
  }, [
    isLearnosityApiReady,
    openReferenceSheetModal,
    referenceSheetModalRef,
    referenceSheetButtonRef,
  ]);

  const onLineReaderClick = useCallback(() => {
    setOpenLineReaderModal((openModal) => !openModal);
  }, []);

  const handleCloseAnnotationsTeacherModal = () => {
    setIsAnnotationTeacherModalOpen(false);
  };

  const annotationToolTipId = 'annotation-tooltip';
  const subjectId = parseInt(subject, 10);

  return (
    <>
      <section
        className={cn(
          styles['player-menu'],
          'flex bb-desktop:ml-auto text-right bb-desktop:mr-[42px] relative bb-desktop:mt-[22px] bb-desktop:mb-[11px] gap-[17px] items-stretch',
        )}
        aria-label="Test Tools"
      >
        {!HighlightsNotesHiddenSubjects.includes(subjectId) && (
          <MenuItemWithToolTip
            containerClass="bb-mobile:hidden"
            withToolTipOnSelected
            toolTipId={annotationToolTipId}
            toolTipDescription={MENU_ITEM_TOOLTIP}
            label="Highlights & Notes"
            icon="highlights_notes"
            ref={annotationRef}
            data-test-id="annotation-menu-item"
            accessibility={{
              'aria-describedby': annotationToolTipId,
            }}
            onClick={onAnnotationClick}
          >
            <img
              className="h-[14px]"
              aria-hidden="true"
              alt="Annotation icon"
              src={highlightsNotesSvg}
              data-test-id="bluebook-calculator-img"
            />
          </MenuItemWithToolTip>
        )}
        {IS_HEADER_MENU_ITEM_CALCULATOR_VISIBLE && (
          <MenuItemWithToolTip
            containerClass="bb-mobile:hidden"
            onClick={onCalculatorClick}
            label="Calculator"
            icon="calculator"
            ref={calculatorRef}
            data-test-id="calculator-menu-item"
          >
            <img
              aria-hidden="true"
              alt="Calculator icon"
              src={calculatorSvg}
              data-test-id="bluebook-calculator-img"
            />
          </MenuItemWithToolTip>
        )}
        {isReferenceSheetMenuVisible(subjectId) ? (
          <MenuItem
            containerClass="bb-mobile:hidden"
            onClick={onReferenceSheetClick}
            label="Reference"
            icon="reference"
            ref={referenceSheetButtonRef}
            data-test-id="reference-menu-item"
            selected={isReferenceSheetButtonSelected}
          >
            <img
              aria-hidden="true"
              alt="Reference icon"
              src={referenceSvg}
              data-test-id="bluebook-reference-img"
            />
          </MenuItem>
        ) : null}
        <MoreMenu
          isPreviewPlayer={isPreviewPlayer}
          toggleExitAssignmentModal={toggleExitAssignmentModal}
          toggleSubmitConfirmDialog={toggleSubmitConfirmDialog}
          toggleHelpModal={toggleHelpModal}
          onLineReaderClick={onLineReaderClick}
          moreButtonRef={moreButtonRef}
          data-test-id="more-menu-item"
          onReferenceSheetClick={onReferenceSheetClick}
          onCalculatorClick={onCalculatorClick}
          onAnnotationClick={onAnnotationClick}
        />
        <MobileMoreMenu
          isPreviewPlayer={isPreviewPlayer}
          toggleExitAssignmentModal={toggleExitAssignmentModal}
          toggleSubmitConfirmDialog={toggleSubmitConfirmDialog}
          toggleHelpModal={toggleHelpModal}
          onLineReaderClick={onLineReaderClick}
          onReferenceSheetClick={onReferenceSheetClick}
          onCalculatorClick={onCalculatorClick}
          onAnnotationClick={onAnnotationClick}
          subjectId={subjectId}
        />
      </section>
      {openCalculatorModal ? (
        <CalculatorModal
          onClose={onCalculatorClose}
          data-test-id="bluebook-calculator-modal"
        />
      ) : null}
      {openReferenceSheetModal ? (
        ReferenceSheetComingSoonSubjects.includes(subjectId) ? (
          <ReferenceSheetInfoModal onClose={onReferenceSheetInfoClose} />
        ) : (
          <ReferenceSheetModal
            ref={referenceSheetModalRef}
            subject={subject}
            defaultWidth={referenceSheet?.lastWidth}
            defaultPosition={referenceSheet?.lastPosition}
            onClose={onReferenceSheetClose}
            data-test-id="bluebook-reference-sheet-modal"
          />
        )
      ) : null}
      {openExitAssignmentModal ? (
        <ExitAssignmentModal
          onClose={toggleExitAssignmentModal}
          save={learnosityApp?.save}
          data-test-id="bluebook-exit-assignment-modal"
          allowLateSubmissionFF={allowLateSubmissionFF}
          allowHardTimerFF={allowHardTimerFF}
          router={router}
          params={params}
        />
      ) : null}
      {openSubmitConfirmDialog && (
        <SubmitConfirmDialog
          setShowSubmitConfirmDialog={toggleSubmitConfirmDialog}
          learnosityApp={learnosityApp}
          isImpersonating={isImpersonating}
          data-test-id="bluebook-submit-confirm-dialog"
          isDigitalExam={isDigitalExam}
        />
      )}
      {openHelpModal && (
        <HelpModal onClose={toggleHelpModal} isDigitalExam={isDigitalExam} />
      )}
      {openLineReaderModal ? (
        <LineReaderModal
          onClose={onLineReaderClose}
          learnosityApp={learnosityApp}
          questions={questions}
        />
      ) : null}
      <HighlightsAndNotesInfoModal
        onClose={setIsHighlightsAndNotesInfoModalVisible}
        isVisible={isHighlightsAndNotesInfoModalVisible}
      />
      {isAnnotationsTeacherModalOpen && (
        <AnnotationsTeacherModal onClose={handleCloseAnnotationsTeacherModal} />
      )}
    </>
  );
}

export default compose(
  withRouter,
  connect(({ featureFlag, userData }) => ({
    allowLateSubmissionFF: featureFlag.allow_late_submission,
    allowHardTimerFF: featureFlag.updated_timer_controls,
    isTeacher: !userData.isStudent,
  })),
)(PlayerMenu);
